import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import jwtDecode from 'jwt-decode';
import apiClient from '../services/apiService';
import { t } from 'i18next';
import i18n from '../i18n';
// Helper function to format error responses
const formatErrorMessage = (error: any): string => {
  // Handle FastAPI validation errors - typically an array of objects with loc, msg, type
  if (error?.response?.data?.detail && Array.isArray(error.response.data.detail)) {
    return error.response.data.detail
      .map((err: any) => err.msg || JSON.stringify(err))
      .join('. ');
  }
  
  // Handle FastAPI validation errors - object with loc, msg, type
  if (error?.response?.data?.detail && typeof error.response.data.detail === 'object' && 'msg' in error.response.data.detail) {
    return error.response.data.detail.msg;
  }
  
  // Handle string error details
  if (error?.response?.data?.detail && typeof error.response.data.detail === 'string') {
    return error.response.data.detail;
  }
  
  // Handle generic error message
  if (error?.message) {
    return error.message;
  }
  
  // Fallback
  return t('errors.genericError');
};
// Define token payload interface
interface TokenPayload {
  sub: number;
  exp: number;
}
// Define role type
type UserRole = 'admin' | 'operator' | 'ingecom';
// Define User interface
interface User {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  is_active: boolean;
  is_admin: boolean;
  role: UserRole;
  language: string;
}
// Define authentication context interface
interface AuthContextProps {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  isAdmin: boolean;
  isOperator: boolean;
}
// Create the auth context
const AuthContext = createContext<AuthContextProps | undefined>(undefined);
// Define provider props interface
interface AuthProviderProps {
  children: ReactNode;
}

// Create the auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize auth state from local storage
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          // Fetch current user data
          const response = await apiClient.get<User>('/auth/me');
          setUser(response.data);
          setToken(storedToken);
          // Sync i18n language from user preference stored in DB
          if (response.data.language) {
            i18n.changeLanguage(response.data.language);
          }
        } catch (error) {
          console.error('Error loading user:', error);
          localStorage.removeItem('token');
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Check if token is expired
  const isTokenExpired = (token: string): boolean => {
    try {
      const decoded = jwtDecode<TokenPayload>(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch {
      return true;
    }
  };

  // Login function
  const login = async (username: string, password: string): Promise<void> => {
    try {
      // Use the specialized login method from the apiClient service
      const response = await apiClient.login(username, password);
      const { access_token } = response.data;

      // Store token in local storage and remove any stale language key
      localStorage.setItem('token', access_token);
      localStorage.removeItem('language');
      
      // Fetch user data with the new token
      const userResponse = await apiClient.get<User>('/auth/me');
      
      setToken(access_token);
      setUser(userResponse.data);
      // Sync i18n language from user preference stored in DB
      if (userResponse.data.language) {
        i18n.changeLanguage(userResponse.data.language);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      // Format the error message before throwing, preserving the original error structure
      // for compatibility with Login.tsx error handling
      const errorMessage = formatErrorMessage(error);
      
      // If we have an error with an Axios-compatible response property, throw that directly
      if (error && typeof error === 'object' && 'response' in error) {
        throw error;
      }
      
      // Otherwise wrap in an Error with the formatted message
      throw new Error(errorMessage);
    }
  };
  // Logout function
  const logout = (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('language');
    setToken(null);
    setUser(null);
  };
  // Check authentication status
  const checkAuth = async (): Promise<boolean> => {
    // If no token exists, not authenticated
    if (!token) return false;
    
    // If token is expired, logout and return false
    if (isTokenExpired(token)) {
      logout();
      return false;
    }
    
    try {
      // Verify token validity with server
      await apiClient.get('/auth/me');
      return true;
    } catch (error) {
      logout();
      return false;
    }
  };
  // Compute role-based permissions
  const isAdmin = !!user && (user.role === 'admin' || user.role === 'ingecom');
  const isOperator = !!user && (user.role === 'operator' || user.role === 'admin' || user.role === 'ingecom');
  // Context value
  const value: AuthContextProps = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    checkAuth,
    isAdmin,
    isOperator
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(t('errors.useAuthError') || 'Auth context error');
  }
  return context;
};